/**
 * Content Tabs - Reusable Tab System
 * Supports multiple tab sections on the same page
 */

(function() {
  'use strict';

  class ContentTabs {
    constructor(container) {
      this.container = container;
      this.nav = container.querySelector('.content-tabs-nav');
      this.navItems = container.querySelectorAll('.content-tabs-nav-item');
      this.panels = container.querySelectorAll('.content-tabs-panel');
      
      if (!this.nav || !this.navItems.length || !this.panels.length) return;
      
      this.init();
    }

    init() {
      this.attachEventListeners();
      
      // Preload all images in all tabs immediately (since they're all in DOM)
      this.preloadAllImages();
      
      // Set initial active state
      this.updateActiveState();
    }

    preloadAllImages() {
      // Preload all images in all tab panels immediately
      this.panels.forEach((panel) => {
        const lazyImages = panel.querySelectorAll('img[data-src]');
        lazyImages.forEach((img) => {
          if (img.dataset.src && !img.src) {
            img.src = img.dataset.src;
            img.classList.add('loaded');
          }
        });
      });
    }

    attachEventListeners() {
      this.navItems.forEach((item) => {
        item.addEventListener('click', (e) => {
          e.preventDefault();
          const targetTab = item.dataset.tab;
          this.switchTab(targetTab);
        });
      });

      // Keyboard navigation
      this.navItems.forEach((item, index) => {
        item.addEventListener('keydown', (e) => {
          let targetIndex = index;
          
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            targetIndex = index > 0 ? index - 1 : this.navItems.length - 1;
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            targetIndex = index < this.navItems.length - 1 ? index + 1 : 0;
          } else if (e.key === 'Home') {
            e.preventDefault();
            targetIndex = 0;
          } else if (e.key === 'End') {
            e.preventDefault();
            targetIndex = this.navItems.length - 1;
          }
          
          if (targetIndex !== index) {
            const targetTab = this.navItems[targetIndex].dataset.tab;
            this.switchTab(targetTab);
            this.navItems[targetIndex].focus();
          }
        });
      });
    }

    switchTab(tabId) {
      // Remove active class from all nav items
      this.navItems.forEach((item) => {
        item.classList.remove('active');
        item.setAttribute('aria-selected', 'false');
      });

      // Hide all panels
      this.panels.forEach((panel) => {
        panel.classList.remove('active');
      });

      // Activate target tab
      const targetNavItem = this.nav.querySelector(`[data-tab="${tabId}"]`);
      const targetPanel = this.container.querySelector(`#${tabId}`);

      if (targetNavItem) {
        targetNavItem.classList.add('active');
        targetNavItem.setAttribute('aria-selected', 'true');
      }

      if (targetPanel) {
        targetPanel.classList.add('active');
      }

      // Scroll active tab into view on mobile
      if (window.innerWidth <= 992 && targetNavItem) {
        targetNavItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    updateActiveState() {
      // Ensure first tab is active if none are
      const hasActive = Array.from(this.navItems).some(item => item.classList.contains('active'));
      if (!hasActive && this.navItems.length > 0) {
        const firstTab = this.navItems[0].dataset.tab;
        this.switchTab(firstTab);
      }
    }
  }

  // Initialize all tab sections on page load
  function initTabs() {
    const tabSections = document.querySelectorAll('.content-tabs-section');
    
    tabSections.forEach((section) => {
      new ContentTabs(section);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTabs);
  } else {
    initTabs();
  }

  // Re-initialize on Shopify section load (for theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.detail.querySelector('.content-tabs-section');
      if (section) {
        new ContentTabs(section);
      }
    });
  }
})();
